import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { fetchPerformance } from './api'
import { formatInputDate, type DateRange } from '@/lib/date-range'
import type { PerformanceQuery } from './schema'
import { aggregationForRange } from './performance-period'

export const reportsKeys = {
  all: ['reports'] as const,
  performance: (query: PerformanceQuery) =>
    [...reportsKeys.all, 'performance', query.aggregation, query.startDate, query.endDate] as const,
}

/** API params for a selected range: ISO dates plus the bucket size for its length. */
function toPerformanceQuery(range: DateRange, today: Date): PerformanceQuery {
  return {
    aggregation: aggregationForRange(range, today),
    startDate: toIsoDate(range.start),
    endDate: toIsoDate(range.end),
  }
}

function toIsoDate(date: Date): string {
  const [day, month, year] = formatInputDate(date).split('/')
  return `${year}-${month}-${day}`
}

/**
 * Performance metrics for the selected date range. The bucket size follows the
 * active tab or the range length (see `aggregationForRange`); all four metrics come back in one
 * response, so the selected metric card is not part of the query.
 */
export function performanceQueryOptions(range: DateRange, today: Date = new Date()) {
  const query = toPerformanceQuery(range, today)
  return queryOptions({
    queryKey: reportsKeys.performance(query),
    queryFn: ({ signal }) => fetchPerformance(query, signal),
    // Keep the previous chart on screen while the next range loads.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}
